import type { PatientExportFilters } from "../Apis/patientExport";
import type { TaskExportFilters } from "../Apis/taskExport";
import type { PatientExportFiltersDraft } from "../components/AgentPages/Configuration/components/ExportData/PatientExportFiltersModal";
import type { TaskExportFiltersDraft } from "../components/AgentPages/Configuration/components/ExportData/TaskExportFiltersModal";
import { toApiDateRangeEnd, toApiDateRangeStart } from "./dateFilterUtils";

const VALID_GENDERS = new Set(["male", "female", "other"]);

/** IST calendar-day bounds — same helpers as Overview list filters. */
function toIso8601FromFilterDate(
  dateStr: string,
  endOfDay: boolean
): string | undefined {
  return endOfDay ? toApiDateRangeEnd(dateStr) : toApiDateRangeStart(dateStr);
}

export function buildPatientExportApiFilters(
  draft: PatientExportFiltersDraft
): PatientExportFilters {
  const filters: PatientExportFilters = {};

  const search = draft.search.trim();
  if (search) filters.search = search;

  if (draft.gender.length === 1 && VALID_GENDERS.has(draft.gender[0])) {
    filters.gender = draft.gender[0] as PatientExportFilters["gender"];
  }

  if (draft.facility.length) filters.facilityId = [...draft.facility];
  if (draft.leadSource.length) filters.leadSource = [...draft.leadSource];

  if (draft.registrationStatus.length === 1) {
    const reg = draft.registrationStatus[0];
    if (reg === "REGISTERED" || reg === "UNREGISTERED") {
      filters.registrationStatus = reg;
    }
  }

  const createdAtFrom = toIso8601FromFilterDate(draft.createdAtFrom, false);
  const createdAtTo = toIso8601FromFilterDate(draft.createdAtTo, true);
  if (createdAtFrom) filters.createdAtFrom = createdAtFrom;
  if (createdAtTo) filters.createdAtTo = createdAtTo;

  return filters;
}

export function buildPatientExportPayload(
  draft: PatientExportFiltersDraft,
  selectedColumnIds: string[],
  email?: string,
  deliveryMode?: import("../Apis/overviewExportTypes").OverviewExportDeliveryMode
) {
  const filters = buildPatientExportApiFilters(draft);
  return {
    selectedColumnIds,
    ...(email ? { email } : {}),
    ...(deliveryMode ? { deliveryMode } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
  };
}

export function buildTaskExportApiFilters(
  draft: TaskExportFiltersDraft
): TaskExportFilters {
  const filters: TaskExportFilters = {};

  const search = draft.search.trim();
  if (search) filters.search = search;

  if (draft.status.length) filters.status = [...draft.status];
  if (draft.priority.length) filters.priority = [...draft.priority];
  if (draft.type.length) filters.workOrderType = [...draft.type];
  if (draft.owner.length === 1) filters.assignedTo = draft.owner[0];
  if (draft.facility.length) filters.facilityId = [...draft.facility];
  if (draft.isOverdue) filters.isOverdue = true;

  const dueDateFrom = toIso8601FromFilterDate(draft.dueDateFrom, false);
  const dueDateTo = toIso8601FromFilterDate(draft.dueDateTo, true);
  if (dueDateFrom) filters.dueDateFrom = dueDateFrom;
  if (dueDateTo) filters.dueDateTo = dueDateTo;

  const createdAtFrom = toIso8601FromFilterDate(draft.createdAtFrom, false);
  const createdAtTo = toIso8601FromFilterDate(draft.createdAtTo, true);
  if (createdAtFrom) filters.createdAtFrom = createdAtFrom;
  if (createdAtTo) filters.createdAtTo = createdAtTo;

  return filters;
}

export function buildTaskExportPayload(
  draft: TaskExportFiltersDraft,
  selectedColumnIds: string[],
  email?: string,
  deliveryMode?: import("../Apis/overviewExportTypes").OverviewExportDeliveryMode
) {
  const filters = buildTaskExportApiFilters(draft);
  return {
    selectedColumnIds,
    ...(email ? { email } : {}),
    ...(deliveryMode ? { deliveryMode } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
  };
}

/** Format date for display in export history (expiry). */
export function formatExportExpiryDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
